import React, { useEffect, useState } from "react";
import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";
import { formatPrice } from "../utils/helpers";
import { getSelectedOptionsKey, getSelectedOptionsList } from "../utils/productOptions";
import { Link } from "react-router-dom";
import api from "../utils/api";

const DEFAULT_MIN_ORDER_VALUE = 200;

const Cart = () => {
  const { cart, updateQuantity, removeFromCart, getCartTotal, getCartCount } = useCart();
  const { exchangeRates, selectedCurrency } = useCurrency();
  const [minimumOrderValue, setMinimumOrderValue] = useState(DEFAULT_MIN_ORDER_VALUE);

  useEffect(() => {
    const loadCheckoutSettings = async () => {
      try {
        const { data } = await api.get("/settings/checkout");
        const minOrderValue = Number(data.settings?.min_order_value ?? DEFAULT_MIN_ORDER_VALUE);
        setMinimumOrderValue(Number.isFinite(minOrderValue) ? minOrderValue : DEFAULT_MIN_ORDER_VALUE);
      } catch (error) {
        console.error("Error fetching checkout settings:", error);
      }
    };

    loadCheckoutSettings();
  }, []);

  const cartTotal = getCartTotal();
  const minimumOrderShortfall = Math.max(0, minimumOrderValue - cartTotal);
  const meetsMinimumOrder = minimumOrderShortfall === 0;

  if (cart.length === 0) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <h1 className="heading-metallic text-3xl mb-4">Your Cart is Empty</h1>
        <p className="text-gray-400 mb-8">Add some products to get started</p>
        <Link to="/optical" className="btn-primary">
          Browse Products
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 sm:py-8">
      <h1 className="heading-metallic mb-6 text-3xl sm:mb-8">Shopping Cart</h1>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
        <div className="lg:col-span-2 space-y-4">
          {cart.map((item) => (
            <div key={item.id ?? `${item.product_id}-${getSelectedOptionsKey(item.selected_options)}`} className="card flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="min-w-0 flex-grow">
                <h3 className="text-lg font-semibold text-white">{item.product?.name || "Product"}</h3>
                <p className="text-gray-400 text-sm">SKU: {item.product?.sku}</p>
                {getSelectedOptionsList(item.selected_options).length > 0 && (
                  <div className="mt-2 space-y-1 text-sm text-gray-400">
                    {getSelectedOptionsList(item.selected_options).map((option) => (
                      <p key={`${option.name}-${option.value}`}>
                        {option.name}: {option.value}
                      </p>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center sm:gap-4">
                <input type="number" min="1" value={item.quantity} onChange={(e) => updateQuantity(item, parseInt(e.target.value, 10))} className="input-field w-full text-center sm:w-20" />

                <p className="text-gold-500 font-semibold text-left sm:w-24 sm:text-right">{item.product?.price ? formatPrice(item.product.price * item.quantity, selectedCurrency, exchangeRates) : "Login to view"}</p>

                <button onClick={() => removeFromCart(item)} className="text-left text-red-500 hover:text-red-400 sm:text-right">
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="card lg:sticky lg:top-24">
          <h3 className="text-xl font-semibold text-white mb-4">Order Summary</h3>
          <div className="space-y-2 mb-6">
            <div className="flex justify-between">
              <span className="text-gray-400">Items ({getCartCount()})</span>
              <span className="text-white">{formatPrice(getCartTotal(), selectedCurrency, exchangeRates)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Shipping</span>
              <span className="text-gray-300">Calculated at checkout</span>
            </div>
            <div className="border-t border-gray-700 pt-2 mt-2">
              <div className="flex justify-between text-lg font-semibold">
                <span className="text-white">Total</span>
                <span className="text-gold-500">{formatPrice(cartTotal, selectedCurrency, exchangeRates)}</span>
              </div>
            </div>
          </div>

          <div className={`mb-6 rounded-lg border px-4 py-3 text-sm ${meetsMinimumOrder ? "border-green-700 bg-green-950 text-green-300" : "border-yellow-700 bg-yellow-950 text-yellow-200"}`}>
            {meetsMinimumOrder
              ? `You have met the minimum order value of ${formatPrice(minimumOrderValue, selectedCurrency, exchangeRates)}.`
              : `Add ${formatPrice(minimumOrderShortfall, selectedCurrency, exchangeRates)} more to reach the minimum order value of ${formatPrice(minimumOrderValue, selectedCurrency, exchangeRates)}.`}
          </div>

          <Link
            to={meetsMinimumOrder ? "/checkout" : "/cart"}
            className={`w-full block text-center ${meetsMinimumOrder ? "btn-primary" : "bg-gray-700 text-gray-300 px-6 py-3 rounded-lg cursor-not-allowed"}`}
            onClick={(event) => {
              if (!meetsMinimumOrder) {
                event.preventDefault();
              }
            }}
          >
            Proceed to Checkout
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Cart;
